/**
 * "Link an agent" (PLAN-AGENT-LINKING §2.2, PLAN-DATA-ONRAMPS §2 row C2).
 *
 * The instructions page. Distinct from `/link` (Link.tsx), which is the consent screen an agent
 * SENDS the customer to. This is the page the customer visits first, from the dashboard.
 *
 * The whole point of the OAuth work is that the instruction is now one URL and no secret. So this
 * page leads with the URL and the single command that consumes it, and puts the manual token lane
 * last, where it belongs: still supported, no longer the front door.
 */
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../api/client.js";
import type { DeploymentConnectionsSummary } from "../api/types.js";
import { Banner, CodeBlock, ErrorNote, Loading, useCopy } from "../components/ui.js";

/** Claude Code's own installer verb, which is the shortest true path for the common case. */
function claudeCodeCommand(mcpUrl: string): string {
  return `claude mcp add --transport http construct ${mcpUrl}`;
}

/**
 * The generic block, for any harness that reads MCP server config as JSON.
 *
 * No `headers` and no token: an OAuth-capable client discovers the authorization server from the
 * endpoint itself and runs the flow. Putting an Authorization header here would be telling the
 * customer to go and find a secret, which is the thing this page exists to stop.
 */
function genericMcpConfig(mcpUrl: string): string {
  return JSON.stringify({ mcpServers: { construct: { type: "http", url: mcpUrl } } }, null, 2);
}

export default function ConnectAgent() {
  const [summary, setSummary] = useState<DeploymentConnectionsSummary | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [loading, setLoading] = useState(true);
  const [copiedUrl, copyUrl] = useCopy();

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setSummary(await api.deploymentConnections());
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  if (loading) return <Loading label="Loading your endpoint" />;
  if (error) return <ErrorNote error={error} onRetry={() => void load()} />;

  const mcpUrl = summary?.endpoint?.mcpUrl ?? null;

  // Not ready is the honest majority case early on, and it has a real answer rather than an empty
  // page: there is nothing to link an agent TO until the Construct is running.
  if (!summary?.ready || !mcpUrl) {
    return (
      <section>
        <h1>Link an agent</h1>
        <Banner kind="info" title="Your Construct is not ready yet">
          An agent connects to your Construct directly, so there is nothing to point one at until it
          is running. This page will show your endpoint as soon as it is.{" "}
          <Link to="/dashboard">Back to your dashboard</Link>.
        </Banner>
      </section>
    );
  }

  return (
    <section>
      <h1>Link an agent</h1>
      <p className="lede">
        Give your agent this one address. It will open your browser, ask you to sign in, and ask you
        what access to grant. You do not copy a key anywhere.
      </p>

      <div className="card" style={{ marginBottom: 24 }}>
        <div className="card__head">
          <h2>Your endpoint</h2>
        </div>
        <div className="card__body">
          <p className="token">{mcpUrl}</p>
          <button type="button" className="btn" onClick={() => copyUrl(mcpUrl)}>
            {copiedUrl ? "Copied" : "Copy address"}
          </button>
          <p className="field__hint">
            This address is yours and it does not change. It is safe to paste into a config file:
            on its own it grants nothing until you approve a request from the consent screen.
          </p>
        </div>
      </div>

      <div className="card" style={{ marginBottom: 24 }}>
        <div className="card__head">
          <h2>Claude Code</h2>
        </div>
        <div className="card__body">
          <CodeBlock code={claudeCodeCommand(mcpUrl)} label="Copy command" />
          <p className="field__hint">
            Run it, and Claude Code opens your browser. Sign in, pick how much access to give, and
            the agent is connected. It appears in{" "}
            <Link to="/connections">your connections</Link> once it collects its credential, and you
            can revoke it there at any time.
          </p>
        </div>
      </div>

      <div className="card" style={{ marginBottom: 24 }}>
        <div className="card__head">
          <h2>Any other MCP client</h2>
        </div>
        <div className="card__body">
          <p>
            Codex and anything else that speaks MCP take the same address. Most clients accept a
            config block like this one:
          </p>
          <CodeBlock code={genericMcpConfig(mcpUrl)} label="Copy config" />
          <p className="field__hint">
            There is no key in that block on purpose. A client that supports the MCP authorization
            spec finds its way to the sign-in screen from the address alone.
          </p>
        </div>
      </div>

      <div className="card" style={{ marginBottom: 24 }}>
        <div className="card__head">
          <h2>An agent with no browser</h2>
        </div>
        <div className="card__body">
          <p>
            On a server or in a container, an agent cannot open a browser. It will print a short
            code instead and ask you to go somewhere and enter it. That somewhere is{" "}
            <Link to="/link">the linking page</Link>, on any device you are signed in on.
          </p>
          <p className="field__hint">
            The code is good for ten minutes and can be used once. If it expires, ask the agent to
            start again.
          </p>
        </div>
      </div>

      <div className="card">
        <div className="card__head">
          <h2>If your agent cannot do any of that</h2>
        </div>
        <div className="card__body">
          <p>
            Some tools only accept a fixed key in a header. For those, mint one by hand in{" "}
            <Link to="/connections">your connections</Link> and paste it into the tool's config. It
            works, and it is the older way of doing this: the key is shown once, it does not expire
            on its own, and revoking it is something you have to remember to do.
          </p>
        </div>
      </div>
    </section>
  );
}
